/**
 * Signetix API v1 resource shapes — mirrors docs/API.md "Resource shapes".
 * Keep in sync with the contract; do not invent fields here (UI-only helpers
 * live next to the features that need them).
 */

export type ULID = string

/* ---------------------------------------------------------------- envelopes */

export interface DataEnvelope<T> {
  data: T
}

export interface CursorMeta {
  per_page: number
  next_cursor: string | null
  prev_cursor: string | null
}

export interface Paginated<T> {
  data: T[]
  meta: CursorMeta
}

export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  errors?: Record<string, string[]>
  /** quota-exceeded */
  quota?: string
  limit?: number | null
  /** feature-unavailable */
  feature?: string
  [extra: string]: unknown
}

/* -------------------------------------------------------------- core shapes */

export interface User {
  id: ULID
  name: string
  email: string
  avatar_url: string | null
  two_factor_enabled: boolean
  is_platform_admin: boolean
  created_at: string
}

export interface Tenant {
  id: ULID
  name: string
  slug: string
  status: 'active' | 'suspended'
  timezone: string
  locale: string
  logo_url: string | null
  /** Phase 12: null without plan feature `custom_branding`. */
  branding?: TenantBranding | null
  settings: Record<string, unknown>
  /** Phase 6: organization default / fallback channel (optional for older payloads, e.g. admin lists). */
  default_channel_id?: ULID | null
  fallback_channel_id?: ULID | null
  created_at: string
}

export interface Membership {
  tenant: { id: ULID; name: string; slug: string }
  role: { id: ULID; name: string; key: string | null }
}

export interface Me {
  user: User
  current_tenant: Tenant | null
  memberships: Membership[]
  /** effective permission keys in current tenant */
  permissions: string[]
  subscription: Subscription | null
  onboarding: Onboarding | null
  impersonating: { by: { id: ULID; name: string } } | null
  /** Phase B (VARIABLES.md §5): the member's location / display-group scopes (older servers omit it). */
  membership_scopes?: MembershipScopes
}

export interface MembershipScopes {
  /** True when the member has scopes: scope-aware permissions only apply inside them. */
  restricted: boolean
  locations: ULID[]
  display_groups: ULID[]
}

export const LIMIT_KEYS = [
  'displays',
  'locations',
  'players',
  'users',
  'storage_bytes',
  'upload_bytes',
  'analytics_retention_days',
] as const
export type LimitKey = (typeof LIMIT_KEYS)[number]

export const FEATURE_KEYS = [
  'advanced_scheduling',
  'campaigns',
  'channels',
  'custom_roles',
  'api_access',
  'proof_of_play',
  'screenshots',
  'data_sources',
  'emergency_broadcasts',
  'custom_branding',
  'approval_workflow',
  'flows',
] as const
export type FeatureKey = (typeof FEATURE_KEYS)[number]

export interface Plan {
  id: ULID
  code: string
  name: string
  description: string | null
  price_monthly_cents: number
  price_yearly_cents: number
  currency: string
  is_public: boolean
  trial_days: number
  sort_order: number
  features: Partial<Record<FeatureKey, boolean>>
  /** null = unlimited */
  limits: Partial<Record<LimitKey, number | null>>
}

export interface EntitlementLimit {
  limit: number | null
  used: number
  remaining: number | null
}

export interface Entitlements {
  features: Record<FeatureKey, boolean>
  limits: Record<LimitKey, EntitlementLimit>
}

export type BillingInterval = 'monthly' | 'yearly'

export interface Subscription {
  id: ULID
  plan: Plan
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'suspended'
  billing_interval: BillingInterval
  trial_ends_at: string | null
  current_period_end: string | null
  entitlements: Entitlements
}

export type OnboardingStepKey =
  | 'account'
  | 'organization'
  | 'plan'
  | 'location'
  | 'display'
  | 'pair_player'
  | 'media'
  | 'slideshow'
  | 'channel'
  | 'schedule'

export type OnboardingStepStatus = 'complete' | 'skipped' | 'pending'

export interface OnboardingStep {
  key: OnboardingStepKey
  title: string
  status: OnboardingStepStatus
  /** false = feature ships in a later phase ("coming soon") */
  available: boolean
}

export interface Onboarding {
  completed: boolean
  dismissed: boolean
  /** 0..100 */
  progress: number
  steps: OnboardingStep[]
}

export interface Location {
  id: ULID
  name: string
  address: string | null
  city: string | null
  postal_code: string | null
  /** ISO-3166 alpha-2 */
  country: string | null
  timezone: string
  latitude: number | null
  longitude: number | null
  description: string | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  displays_count: number
  /** Phase 6: location default / fallback channel. */
  default_channel_id: ULID | null
  fallback_channel_id: ULID | null
  created_at: string
  updated_at: string
}

export type SystemRoleKey = 'owner' | 'administrator' | 'content_manager' | 'scheduler' | 'display_manager' | 'viewer' | 'location_manager'

export interface Role {
  id: ULID
  key: string | null
  name: string
  description: string | null
  is_system: boolean
  permissions: string[]
  users_count: number
}

export interface Member {
  id: ULID
  name: string
  email: string
  role: { id: ULID; name: string; key: string | null }
  two_factor_enabled: boolean
  joined_at: string
  /** Phase B: location / display-group scopes (empty = unrestricted; older servers omit it). */
  scopes?: MemberScope[]
}

export type MemberScopeType = 'location' | 'display_group'

export interface MemberScope {
  type: MemberScopeType
  id: ULID
  name: string
}

export interface Invitation {
  id: ULID
  email: string
  role: { id: ULID; name: string }
  invited_by: { id: ULID; name: string } | null
  expires_at: string
  created_at: string
}

export interface InvitationPreview {
  tenant_name: string
  email: string
  role_name: string
  expires_at: string
}

export interface Session {
  id: string
  ip_address: string | null
  user_agent: string | null
  last_active_at: string
  is_current: boolean
}

export interface Token {
  id: string
  name: string
  last_used_at: string | null
  created_at: string
}

export type AuditActorType = 'user' | 'player' | 'system' | 'platform_admin' | 'api_key' | 'flow'

export interface AuditLog {
  id: ULID
  action: string
  actor: { id: ULID; name: string; email: string } | null
  actor_type: AuditActorType
  /** Phase F: non-member actors (`flow` → `Signetix Flow (#<flow id>)`) carry their own name / id. */
  actor_name?: string | null
  actor_id?: string | null
  resource_type: string | null
  resource_id: string | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

export interface Dashboard {
  displays: { online: number; total: number }
  locations: number
  users: number
  storage: { used_bytes: number; limit_bytes: number | null }
  active_campaigns: number
  active_schedules: number
  players_with_warnings: number
  recent_activity: AuditLog[]
  /* Phase 12 additions (older servers omit them). */
  device_health?: { online: number; offline: number; never_connected: number; with_warnings: number; with_errors: number }
  alerts?: DashboardAlert[]
  currently_playing?: CurrentlyPlaying[]
  upcoming_campaigns?: UpcomingCampaign[]
  player_versions?: { version: string | null; count: number }[]
}

export interface DashboardAlert {
  id: string
  type: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  title: string
  body: string
  url: string | null
  created_at: string
}

export interface NamedRef {
  id: string
  name: string | null
}

export interface CurrentlyPlaying {
  display: NamedRef
  mode: 'normal' | 'fallback' | 'safe_mode' | 'emergency' | 'unpaired' | 'idle' | null
  slideshow: NamedRef | null
  schedule: NamedRef | null
  campaign: NamedRef | null
  slide_id: string | null
  since: string | null
}

export interface UpcomingCampaign {
  id: string
  name: string
  advertiser: string | null
  color: string | null
  start_date: string | null
  end_date: string | null
}

export interface PermissionGroup {
  group: string
  permissions: { key: string; label: string }[]
}

/* -------------------------------------------------------------- auth bodies */

export type LoginResponse = DataEnvelope<User> | { two_factor: true }

export interface TwoFactorSetup {
  secret: string
  qr_svg: string
  otpauth_url: string
}

export interface RecoveryCodes {
  recovery_codes: string[]
}

/* ------------------------------------------------------- platform admin --- */
/*
 * The contract lists admin endpoints without response shapes. These types
 * assume admin tenant resources extend Tenant with optional aggregates; every
 * extra field is optional and the UI renders defensively.
 */

export interface AdminTenant extends Tenant {
  suspended_at?: string | null
  suspension_reason?: string | null
  users_count?: number
  displays_count?: number
  locations_count?: number
  owner?: { id: ULID; name: string; email: string } | null
  subscription?: Pick<Subscription, 'id' | 'status' | 'billing_interval' | 'trial_ends_at' | 'current_period_end'> & {
    plan?: Pick<Plan, 'id' | 'name' | 'code'>
  } | null
}

/** Free-form metrics object; numeric leaves are rendered as KPI tiles. */
export type AdminDashboard = Record<string, unknown>

export interface PlanInput {
  code: string
  name: string
  description: string | null
  price_monthly_cents: number
  price_yearly_cents: number
  currency: string
  is_public: boolean
  trial_days: number
  sort_order: number
  features: Partial<Record<FeatureKey, boolean>>
  limits: Partial<Record<LimitKey, number | null>>
}

/* ------------------------------------------------------------ Phase 2 --- */
/* Displays, players, pairing, commands — docs/API.md "Phase 2 resource shapes". */

export type Connectivity = 'online' | 'offline' | 'unknown'
export type Health = 'ok' | 'warning' | 'error'

export interface Warning {
  code: string
  severity: 'info' | 'warning' | 'error'
  since: string | null
  detail?: string
}

export type PlayerPlatform = 'linux' | 'raspberry_pi' | 'windows' | 'android_tv' | 'tizen' | 'webos'
export type PlayerStatus = 'provisioning' | 'active' | 'disabled' | 'retired'

export interface PlayerSummary {
  id: ULID
  platform: PlayerPlatform
  arch: string
  model: string | null
  player_version: string | null
  status: PlayerStatus
  runtime_state: string | null
  last_heartbeat_at: string | null
  /** `capabilities.commands` of the player; null = unknown (every action allowed). */
  supported_commands?: CommandType[] | null
}

/** Latest heartbeat (hot record); null fields when unknown. */
export interface PlayerTelemetry {
  received_at: string
  state: string
  health: Health
  uptime_s: number | null
  cpu_percent: number | null
  memory_percent: number | null
  temperature_c: number | null
  storage: { total_bytes: number | null; free_bytes: number | null }
  network: { type: string | null; signal_dbm: number | null; local_ip: string | null; rtt_ms: number | null }
  renderer: { status: string | null; crashes_24h: number | null }
  playback: { mode: string | null; playlist_id: string | null; presentation_id: string | null; since: string | null } | null
  clock_skew_ms: number | null
  warnings: Warning[]
}

export type ReleaseChannel = 'stable' | 'beta' | 'internal'

export interface PlayerUpdateInfo {
  effective_channel: ReleaseChannel
  update_slots: 'ab' | 'store' | 'none' | null
  /** The release the manifest currently offers this player (null = up to date or not admitted yet). */
  available: { release_id: ULID; version: string; channel: ReleaseChannel; mandatory: boolean } | null
}

export interface Player extends PlayerSummary {
  hardware_id: string
  os_version: string | null
  protocol_version: number | null
  capabilities: Record<string, unknown> | null
  /** null = follows the organization's release channel (Phase 12). */
  update_channel: ReleaseChannel | null
  pinned_version: string | null
  /** Release state (PLAYER.md §10.3); null for retired players. */
  update?: PlayerUpdateInfo | null
  last_ip: string | null
  claimed_at: string | null
  claimed_by: { id: ULID; name: string } | null
  retired_at: string | null
  retired_reason: string | null
  display: { id: ULID; name: string } | null
  telemetry: PlayerTelemetry | null
}

export type DisplayStatus = 'pending' | 'active' | 'orphaned' | 'archived'
export type Orientation = 'landscape' | 'portrait' | 'landscape_flipped' | 'portrait_flipped'

export interface DisplaySync {
  config_version: number
  schedule_version: number
  content_version: number
  manifest_etag: string | null
  applied_manifest_etag: string | null
  in_sync: boolean
  last_sync_at: string | null
  last_sync_state: 'applied' | 'failed' | 'rolled_back' | null
}

export interface Display {
  id: ULID
  name: string
  description: string | null
  status: DisplayStatus
  location: { id: ULID; name: string; timezone: string } | null
  timezone: string | null
  effective_timezone: string
  orientation: Orientation
  resolution: { width: number; height: number } | null
  connectivity: Connectivity
  last_heartbeat_at: string | null
  connectivity_changed_at: string | null
  health: Health | null
  warnings: Warning[]
  groups: { id: ULID; name: string; color: string | null }[]
  tags: { id: ULID; name: string; slug: string; color: string | null }[]
  /** `telemetry` is only present on the detail endpoint. */
  player: (PlayerSummary & { telemetry?: PlayerTelemetry | null }) | null
  sync: DisplaySync
  config: { heartbeat_s: number; screenshot_policy?: ScreenshotPolicy }
  /** Phase 6: display default / fallback channel (own value only; see effective content helpers). */
  default_channel_id: ULID | null
  fallback_channel_id: ULID | null
  created_at: string
  updated_at: string
}

export interface DisplaySummary {
  total: number
  online: number
  offline: number
  unknown: number
  pending: number
  warnings: number
}

export interface DisplayAssignment {
  id: ULID
  player: PlayerSummary
  assigned_at: string
  unassigned_at: string | null
  reason: string
  close_reason: string | null
  assigned_by: { id: ULID; name: string } | null
}

export const COMMAND_TYPES = [
  'sync',
  'reload',
  'restart_renderer',
  'restart_player',
  'reboot_device',
  'clear_cache',
  'screenshot',
  'diagnostics',
  'update_player',
  'rotate_credentials',
  'identify',
  'start_setup',
] as const
export type CommandType = (typeof COMMAND_TYPES)[number]

export type CommandStatus = 'pending' | 'delivered' | 'acknowledged' | 'started' | 'completed' | 'failed' | 'expired' | 'canceled'

export interface DisplayCommand {
  id: ULID
  /** One of CommandType; typed loosely as the server may add types. */
  type: string
  payload: Record<string, unknown>
  status: CommandStatus
  issued_by: { id: ULID; name: string } | null
  player_id: ULID
  expires_at: string
  delivered_at: string | null
  acknowledged_at: string | null
  started_at: string | null
  completed_at: string | null
  result: Record<string, unknown> | null
  error_code: string | null
  error_message: string | null
  created_at: string
}

export interface DisplayGroup {
  id: ULID
  name: string
  description: string | null
  color: string | null
  displays_count: number
  /** Phase B: breaks ties when a display is in several groups that set the same variable (−1000..1000). */
  variable_priority?: number
}

export interface Tag {
  id: ULID
  name: string
  slug: string
  color: string | null
  displays_count: number
}

/** Capabilities `screens[]` entry (PLAYER_PROTOCOL.md §11); every field optional for robustness. */
export interface PairingScreen {
  id?: string | number
  connected?: boolean
  width?: number
  height?: number
  refresh_hz?: number
  rotation?: number
  [extra: string]: unknown
}

export interface PairingLookup {
  platform: PlayerPlatform
  arch: string
  model: string | null
  player_version: string | null
  screens: PairingScreen[]
  expires_at: string
}

export type ClaimMode = 'new' | 'attach' | 'replace'

export interface NewDisplayClaimInput {
  name: string
  location_id?: ULID | null
  group_ids?: ULID[]
  tag_ids?: ULID[]
  orientation?: Orientation
  timezone?: string | null
}

export type PairingClaimRequest =
  | { mode: 'new'; code: string; nonce: string | null; display: NewDisplayClaimInput }
  | { mode: 'attach'; code: string; nonce: string | null; display_id: ULID }
  | { mode: 'replace'; code: string; nonce: string | null; display_id: ULID; reason?: string | null }

export interface PairingClaimResult {
  mode: ClaimMode
  player: Player
  display: Display
}

/* ------------------------------------------------------------ Phase 3 --- */
/* Media library — docs/API.md "Phase 3 resource shapes". */

export const MEDIA_KINDS = ['image', 'video', 'audio', 'svg', 'pdf', 'html_package', 'font'] as const
export type MediaKind = (typeof MEDIA_KINDS)[number]

export const MEDIA_STATUSES = ['uploading', 'processing', 'ready', 'failed', 'rejected'] as const
export type MediaStatus = (typeof MEDIA_STATUSES)[number]

export interface MediaVariant {
  variant: string
  mime: string
  size_bytes: number
  width: number | null
  height: number | null
  duration_ms: number | null
  codec: string | null
  is_player_rendition: boolean
}

export interface Media {
  id: ULID
  name: string
  original_filename: string
  kind: MediaKind
  mime: string
  size_bytes: number
  sha256: string | null
  status: MediaStatus
  failure_code: string | null
  failure_detail: string | null
  duplicate_of: ULID | null
  width: number | null
  height: number | null
  duration_ms: number | null
  frame_rate: number | null
  video_codec: string | null
  audio_codec: string | null
  bitrate: number | null
  page_count: number | null
  has_audio: boolean | null
  revision: number
  folder: { id: ULID; name: string } | null
  tags: { id: ULID; name: string; slug: string; color: string | null }[]
  valid_from: string | null
  valid_until: string | null
  uploaded_by: { id: ULID; name: string } | null
  /** Signed (~15 min); null until processed. */
  thumbnail_url: string | null
  /** Larger image / poster / playable rendition. */
  preview_url: string | null
  preview_kind: 'image' | 'video' | 'audio' | null
  /** SVG elements/attributes removed for safety. */
  sanitised: { removed: string[] } | null
  created_at: string
  updated_at: string
  processed_at: string | null
  deleted_at: string | null
  purge_after: string | null
  /** Detail endpoint only. */
  variants?: MediaVariant[]
  metadata?: Record<string, unknown>
}

export interface UploadPartUrl {
  part_number: number
  url: string
  method: 'PUT'
  headers: Record<string, string>
  expires_at: string
}

export type UploadIntentStatus = 'initiated' | 'completed' | 'processed' | 'failed' | 'aborted' | 'expired'

export interface UploadIntent {
  upload_id: ULID
  media_id: ULID
  /** Outcome of a replacement is reported here, never on the live media. */
  status: UploadIntentStatus | (string & {})
  failure_code: string | null
  failure_detail: string | null
  multipart: boolean
  part_size: number
  part_count: number
  parts: UploadPartUrl[]
  expires_at: string
  media: Media
}

export interface MediaFolder {
  id: ULID
  name: string
  parent_id: ULID | null
  path: string
  depth: number
  media_count: number
}

export interface MediaSummary {
  count: number
  by_kind: Partial<Record<MediaKind, number>>
  storage: { used_bytes: number; reserved_bytes: number; limit_bytes: number | null }
}

export interface MediaUsage {
  slideshows: { id: ULID; name: string; in_draft?: boolean; published_revisions?: number[] }[]
  on_screens: number
}

export type MediaBulkAction = 'move' | 'delete' | 'restore' | 'tag'

export interface MediaBulkResult {
  succeeded: ULID[]
  failed: { id: ULID; type: string; detail: string }[]
}

/* ------------------------------------------------------------ Phase 4 --- */
/* Slideshows, drafts, publishing — docs/API.md "Phase 4 resource shapes". */

export const ELEMENT_TYPES = ['text', 'image', 'video', 'shape', 'icon', 'clock', 'date', 'weather', 'qr', 'rss', 'ticker', 'web', 'html'] as const
export type ElementType = (typeof ELEMENT_TYPES)[number]

export type BackgroundFit = 'contain' | 'cover' | 'fill' | 'none'

export interface DraftGradient {
  type: 'linear' | 'radial'
  angle?: number
  stops: { offset: number; color: string }[]
}

export interface DraftBackground {
  color?: string | null
  gradient?: DraftGradient | null
  image?: { media_id: ULID; fit?: BackgroundFit; opacity?: number } | null
  video?: { media_id: ULID; fit?: BackgroundFit } | null
}

export interface ZoneRect {
  x: number
  y: number
  w: number
  h: number
}

export interface DraftZone {
  /** `^[a-z][a-z0-9_-]{0,31}$` */
  id: string
  name?: string | null
  rect: ZoneRect
  z: number
  primary?: boolean
  background?: DraftBackground | null
}

export interface DraftElement {
  id?: ULID
  type: ElementType | (string & {})
  x: number
  y: number
  width: number
  height: number
  rotation: number
  opacity: number
  visible: boolean
  locked: boolean
  /** image, video, icon (svg), html (package); pdf page via props.page */
  media_id?: ULID | null
  /** PRESENTATION_SCHEMA.md §7.2 properties (snake_case), minus asset refs. */
  props: Record<string, unknown>
  editor_meta?: Record<string, unknown> | null
}

export type TransitionType = 'none' | 'fade' | 'slide' | 'zoom'
export type TransitionDirection = 'left' | 'right' | 'up' | 'down'

export interface DraftTransition {
  type: TransitionType
  duration_ms?: number
  direction?: TransitionDirection
  easing?: 'linear' | 'ease_in_out'
}

export interface DraftSlide {
  id?: ULID
  zone_id: string
  name?: string | null
  duration_ms: number
  transition: DraftTransition
  background?: DraftBackground | null
  valid_from?: string | null
  valid_until?: string | null
  enabled: boolean
  elements: DraftElement[]
}

export type SlideshowOrientation = 'landscape' | 'portrait' | 'landscape_flipped' | 'portrait_flipped'

/** Document-local presentation rule (SIGNETIX_LOGIC.md §3.1): referenced by element `props.logic` / `props.variants`. */
export interface DraftRule {
  /** `^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$` */
  id: string
  name?: string | null
  /** A `signetix.rule` v1 document. */
  rule: Record<string, unknown>
}

export interface SlideshowDraft {
  lock_version: number
  resolution: { width: number; height: number }
  orientation: SlideshowOrientation
  background: DraftBackground | null
  zones: DraftZone[]
  slides: DraftSlide[]
  /** Phase D: presentation rules (≤ 100). Absent on older servers. */
  rules?: DraftRule[]
}

export type SlideshowStatus = 'draft' | 'pending_approval' | 'approved' | 'published' | 'archived'

export interface UserRef {
  id: ULID
  name: string
}

export interface SlideshowLock {
  user: UserRef
  client_id: string
  expires_at: string
  is_mine: boolean
}

export interface Slideshow {
  id: ULID
  name: string
  description: string | null
  status: SlideshowStatus
  resolution: { width: number; height: number }
  orientation: SlideshowOrientation | (string & {})
  zones_count: number
  slides_count: number
  has_unpublished_changes: boolean
  latest_revision: number
  published_version: { id: ULID; revision: number; published_at: string; published_by: UserRef | null } | null
  lock: SlideshowLock | null
  approval: {
    requested_by: UserRef | null
    requested_at: string | null
    approved_by: UserRef | null
    approved_at: string | null
    comment: string | null
  } | null
  requires_approval: boolean
  thumbnail_url: string | null
  created_by: UserRef | null
  created_at: string
  updated_at: string
}

export interface SlideshowVersion {
  id: ULID
  revision: number
  schema_version: number
  sha256: string
  size_bytes: number
  element_types: string[]
  asset_count: number
  published_by: UserRef | null
  published_at: string
  notes: string | null
  is_current: boolean
}

export interface CompileIssue {
  path: string
  message: string
}

export interface CompileResult {
  valid: boolean
  errors: CompileIssue[]
  /** PresentationDocument (packages/presentation-schema) — may be partial when invalid. */
  document: Record<string, unknown> | null
  /** sha256 → signed URL (preview only) */
  asset_urls: Record<string, string>
}

export interface VersionDocument {
  document: Record<string, unknown>
  asset_urls: Record<string, string>
}

/* ------------------------------------------------------------ Phase 6 --- */
/* Channels and default content — docs/API.md "Phase 6 endpoints", CHANNELS.md §9. */

export const CHANNEL_LAYERS = ['tenant', 'location', 'group', 'display'] as const
export type ChannelLayer = (typeof CHANNEL_LAYERS)[number]
export type ScopedLayer = Exclude<ChannelLayer, 'tenant'>
export type InsertionPolicy = 'append' | 'prepend' | 'interleave'

export interface Channel {
  id: ULID
  name: string
  description: string | null
  item_count: number
  content_version: number
  created_at: string
  updated_at: string
}

export interface ChannelItem {
  id: ULID
  position: number
  item_type: 'slideshow' | 'channel' | 'slot'
  slideshow: { id: ULID; name: string; published_revision: number | null } | null
  /** Pinned version; null = follow the latest published version. */
  slideshow_version: { id: ULID; revision: number } | null
  child_channel: { id: ULID; name: string } | null
  layer: ChannelLayer
  scope: { type: string; id: ULID; name: string } | null
  insertion: InsertionPolicy
  interleave_every: number | null
  duration_override_ms: number | null
  valid_from: string | null
  valid_until: string | null
  /** Signetix Logic (SIGNETIX_LOGIC.md §5): library rule gating this item; absent on older servers. */
  condition?: RuleCondition | null
  /** Phase E: summary for `item_type: 'slot'` (CONTENT_SLOTS.md §7); null/absent otherwise. */
  slot?: import('@/features/content-slots/types').ChannelItemSlotSummary | null
}

export interface ChannelPreviewItem {
  /** Uppercase item id, or a `/`-joined path for items expanded from nested channels. */
  id: string
  slideshow: { id: ULID | null; name: string | null }
  revision: number | null
  duration_ms: number | null
  valid_from: string | null
  valid_until: string | null
  /** `channel_id` is uppercase (manifest form). */
  source: { channel_id: string; layer: ChannelLayer | (string & {}) }
  /** EDGE_AUTOMATION.md §5: the item's conditions (`unknown` without `display_id`); absent on older servers. */
  conditions?: ConditionCheck[]
  /** All conditions pass (null: unconditional). */
  condition_passed?: boolean | null
}

export type ChannelSkipReason = 'expired' | 'not_published' | (string & {})

export interface ChannelPreview {
  items: ChannelPreviewItem[]
  skipped: { item_id: ULID; reason: ChannelSkipReason }[]
  /** True when a display was given and conditions were evaluated against its edge context. */
  conditions_evaluated?: boolean
  conditions?: ConditionEvaluation[]
}

/** `409 channel-in-use` extension. */
export interface ChannelUsedBy {
  channels: string[]
  assignments: number
  /** Phase 7: names of schedules playing the channel. */
  schedules: string[]
}

/* ------------------------------------------------------------ Phase 7 --- */
/* Campaigns, schedules, emergency broadcasts, calendar, simulator — docs/API.md "Phase 7 endpoints". */

export const CAMPAIGN_STATUSES = ['draft', 'active', 'paused', 'archived'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]
export type CampaignDerivedStatus = 'draft' | 'paused' | 'archived' | 'scheduled' | 'active' | 'completed'

export interface Campaign {
  id: ULID
  name: string
  code: string | null
  advertiser: string | null
  description: string | null
  start_date: string | null
  end_date: string | null
  priority: number
  /** `#RRGGBB` */
  color: string | null
  status: CampaignStatus
  /** Dates evaluated in the organization's zone. */
  derived_status: CampaignDerivedStatus
  schedules_count: number
  /** Signetix Logic (SIGNETIX_LOGIC.md §5). */
  condition?: RuleCondition | null
  created_at: string
  updated_at: string
}

export const SCHEDULE_RECURRENCES = ['once', 'daily', 'weekly', 'monthly_dom'] as const
export type ScheduleRecurrence = (typeof SCHEDULE_RECURRENCES)[number]

export const SCHEDULE_TARGET_TYPES = ['tenant', 'location', 'display_group', 'tag', 'display'] as const
export type ScheduleTargetType = (typeof SCHEDULE_TARGET_TYPES)[number]

export type ScheduleStatus = 'disabled' | 'paused' | 'expired' | 'future' | 'active' | 'scheduled'

export interface Schedule {
  id: ULID
  name: string
  campaign: { id: ULID; name: string; color: string | null; status: string } | null
  content: { type: 'channel' | 'slideshow'; id: ULID; name: string | null }
  priority: number
  start_date: string | null
  end_date: string | null
  /** `HH:MM` (seconds trimmed); null = 00:00. */
  start_time: string | null
  /** Exclusive; `24:00` and `00:00` mean end of day; null = end of day. */
  end_time: string | null
  /** ISO bitmask: bit 0 = Monday … bit 6 = Sunday. */
  weekdays: number
  recurrence: ScheduleRecurrence
  days_of_month: number[] | null
  /** `display` or an IANA zone. */
  timezone: string
  valid_from: string | null
  valid_until: string | null
  is_enabled: boolean
  version: number
  targets: { type: ScheduleTargetType | (string & {}); id: ULID | null; name: string | null }[]
  /** Approximate, in the organization's zone — the per-display simulator is exact. */
  status: ScheduleStatus
  /** Signetix Logic (SIGNETIX_LOGIC.md §5). */
  condition?: RuleCondition | null
  created_at: string
  updated_at: string
}

export type EmergencySeverity = 'info' | 'warning' | 'critical'
export type EmergencyStatus = 'scheduled' | 'active' | 'expired' | 'ended'

export interface EmergencyBroadcast {
  id: ULID
  name: string
  content_type: 'slideshow' | 'message'
  slideshow: { id: ULID; name: string; version_id: ULID; revision: number } | null
  message: { title: string; body?: string | null; severity?: EmergencySeverity | null } | null
  all_displays: boolean
  targets: { type: string; id: ULID }[]
  priority: number
  status: EmergencyStatus
  starts_at: string
  ends_at: string
  ended_at: string | null
  started_by: { id: ULID; name: string } | null
  created_at: string
}

export type SimulationSource = 'schedule' | 'default' | 'fallback'

export interface SimulationSequenceItem {
  presentation: string
  slideshow_id: ULID | null
  slideshow: string | null
  revision: number | null
  entry_id?: string | null
  ref?: string | null
  item_index?: number
  duration_ms?: number | null
}

/** Tri-state rule result (RULE_ENGINE.md §5). */
export type ConditionResult = 'true' | 'false' | 'unknown'

/** Rule engine leaf trace (RULE_ENGINE.md §9). */
export interface ConditionTraceLeaf {
  path: string
  ref: string
  op: string
  result: ConditionResult
  reason: string
  resolved?: unknown
  freshness?: string | null
}

/** One condition of a consumer (entry / playlist item) with its result after `unknown_as`. */
export interface ConditionCheck {
  /** Upper-case rule id (manifest form). */
  rule_id: string
  unknown_as: boolean
  result: ConditionResult
  passed: boolean
}

/** One rule evaluated against the display's edge context (per campaign scope) — EDGE_AUTOMATION.md §5. */
export interface ConditionEvaluation {
  rule_id: string
  name: string | null
  /** Campaign scope (upper case) for rules referencing `var.*` / `campaign.*`; null otherwise. */
  campaign_id: string | null
  /** Campaign name for `campaign_id` (simulator only). */
  campaign?: string | null
  result: ConditionResult
  edge: boolean
  reason: string
  trace: ConditionTraceLeaf[]
}

export interface SimulationEvaluation {
  /** Entry id — uppercase (manifest form); compare case-insensitively with resource ids. */
  id: string
  status: 'winner' | 'suppressed' | 'invalid_rule' | 'unknown_timezone' | 'cancelled' | 'not_yet_valid' | 'expired' | 'inactive' | 'condition_false' | 'no_playable_content' | (string & {})
  detail: string
  name?: string
  kind?: 'schedule' | 'emergency'
  campaign?: string | null
  class?: number
  priority?: number
  zone?: string
  occurrence_day?: string | null
  start_instant?: string | null
  /** The entry's conditions (campaign condition first); empty when unconditional. Absent on older servers. */
  conditions?: ConditionCheck[]
}

/** A playlist item of the decided content left out because a condition does not hold. */
export interface SimulationSkippedItem extends SimulationSequenceItem {
  conditions: ConditionCheck[]
}

/** `GET /displays/{id}/simulate` — SCHEDULING.md §12 plus names. */
export interface Simulation {
  at: string
  source: SimulationSource
  tier: { class: number; priority: number }
  entries: { id: string; kind: 'schedule' | 'emergency'; name: string; campaign: string | null }[]
  ref: string | null
  fallback_source: string | null
  playlist: string | null
  playlists: string[]
  sequence: SimulationSequenceItem[]
  evaluations: SimulationEvaluation[]
  display_timezone: string
  /** Wall clock without zone, e.g. `2026-10-08T11:00:00`. */
  display_local_time: string | null
  next_transition: string | null
  stale: boolean
  /** Every rule evaluated at `at` (EDGE_AUTOMATION.md §5); absent on older servers. */
  conditions?: ConditionEvaluation[]
  skipped_items?: SimulationSkippedItem[]
}

export type ConflictKind = 'shared_airtime' | 'shadowed' | 'never_active' | 'dst_edge'

/** `GET /displays/{id}/conflicts` — SCHEDULING.md §11 with entry names. */
export interface ScheduleConflict {
  kind: ConflictKind
  severity: 'info' | 'warning' | 'error'
  entries: { id: string; name: string }[]
  shadowed_by?: { id: string; name: string }[]
  intervals?: { from: string; to: string }[]
  message: string
}

export interface CalendarOccurrence {
  /** `<schedule id>@<start>` */
  id: string
  kind: 'schedule' | 'emergency'
  schedule_id?: ULID | null
  emergency_id?: ULID | null
  name: string
  priority: number
  start: string
  end: string
  status: 'past' | 'active' | 'future'
  all_day: boolean
  recurrence: ScheduleRecurrence | null
  campaign: { id: ULID; name: string; color: string | null } | null
  content: { type: string; id: ULID; name: string | null } | null
  /** Conflict kinds — only with `display_id`. */
  conflicts?: ConflictKind[]
}

/** `GET /calendar` */
export interface ScheduleCalendar {
  timezone: string
  from: string
  to: string
  occurrences: CalendarOccurrence[]
}

/** `409 slideshow-in-use` extension (Phase 6 channels, Phase 7 schedules). */
export interface SlideshowUsedBy {
  channels: string[]
  schedules: string[]
  emergency_broadcasts: string[]
}

/* ------------------------------------------------- Phase 8: device operations */

/** `GET /displays/{id}/screenshots` — newest first; `url` is a 10-minute signed URL. */
export interface Screenshot {
  id: ULID
  trigger: 'command' | 'interval'
  command_id: ULID | null
  mime: string
  width: number | null
  height: number | null
  size_bytes: number
  captured_at: string
  url: string
}

export type EventSeverity = 'debug' | 'info' | 'warning' | 'error' | 'critical'

/** `GET /displays/{id}/events` — player-reported events (PLAYER_PROTOCOL.md §14), newest `occurred_at` first. */
export interface DisplayEvent {
  id: ULID
  player_id: ULID
  /** A `PlayerEventType`; unknown types are stored and shown verbatim. */
  type: string
  severity: EventSeverity
  message: string | null
  data: Record<string, unknown> | null
  occurred_at: string
  received_at: string
  clock_untrusted: boolean
}

/** `GET /players/{id}/diagnostics` — stored bundles, newest first; `url` is a 10-minute signed URL. */
export interface DiagnosticsBundle {
  id: ULID
  command_id: ULID | null
  size_bytes: number
  sha256: string
  created_at: string
  url: string
}

/* ------------------------------------------------------ Phase 11: data sources */

export const DATA_SOURCE_TYPES = ['json_url', 'rest_json', 'rss', 'csv', 'weather', 'datetime'] as const
export type DataSourceType = (typeof DATA_SOURCE_TYPES)[number]
export type DataSourceStatus = 'pending' | 'ok' | 'error' | 'disabled'

export interface JsonUrlConfig {
  url: string
}
export interface RestJsonConfig {
  url: string
  method?: 'GET' | 'POST'
  query?: Record<string, string>
  body?: unknown
  /** Dot / `[i]` path to the part of the response to keep. */
  root?: string | null
}
export interface RssConfig {
  url: string
  /** 1–50, default 20. */
  max_items?: number
  include_images?: boolean
}
export interface CsvConfig {
  url: string
  delimiter?: ',' | ';' | '\t'
  has_header?: boolean
}
export interface WeatherConfig {
  latitude: number
  longitude: number
  location_name: string
  units?: 'metric' | 'imperial'
  /** 0–7. */
  forecast_days?: number
}
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
/** `HH:MM` (close may be `24:00`; close ≤ open = runs past midnight). */
export interface OpeningInterval {
  open: string
  close: string
}
export interface DateTimeHoliday {
  /** `YYYY-MM-DD`. */
  date: string
  name?: string | null
  /** Special hours; empty / absent = closed all day. */
  hours?: OpeningInterval[]
}
/** `datetime` — a local source (nothing is fetched); values are computed on the players. */
export interface DateTimeSourceConfig {
  /** IANA zone; null = the display's zone. */
  timezone?: string | null
  /** Default `["sat", "sun"]`. */
  weekend_days?: Weekday[]
  /** ≤ 4 intervals per day; a missing / empty day is closed. */
  opening_hours?: Partial<Record<Weekday, OpeningInterval[]>>
  /** ≤ 400. */
  holidays?: DateTimeHoliday[]
}
export type DataSourceConfig = JsonUrlConfig | RestJsonConfig | RssConfig | CsvConfig | WeatherConfig | DateTimeSourceConfig

/** Computed values of a `datetime` source (`{{ shop.is_open }}`…). */
export interface DateTimeValues {
  now: string
  date: string
  time: string
  hour: number
  minute: number
  weekday: string
  weekday_number: number
  day: number
  month: number
  year: number
  is_weekend: boolean
  is_holiday: boolean
  holiday_name: string | null
  is_open: boolean
  opens_at: string | null
  closes_at: string | null
  hours_today: string
  timezone: string | null
}

export type OfflineThen = 'fallback' | 'default' | 'hide'
/** What screens do when the source can't be refreshed. */
export interface DataSourceOffline {
  /** Seconds the last value keeps showing after it stops being fresh; null = forever (max 2592000). */
  keep_last_s: number | null
  then: OfflineThen
  /** Required when `then` = `default` (≤ 500 chars). */
  default_value: string | null
}

export type FreshnessState = 'fresh' | 'stale' | 'expired' | 'unavailable'
export interface DataSourceFreshness {
  state: FreshnessState
  fetched_at: string | null
  age_s: number | null
  stale_at: string | null
  expires_at: string | null
}

export type AuthPreset = 'none' | 'bearer' | 'basic' | 'api_key_header' | 'api_key_query' | 'custom'

/**
 * Partial secrets update (`secrets_patch`, exclusive with `secrets`): a value replaces that
 * entry (header names case-insensitive), null removes it, omitted entries are kept.
 */
export interface DataSourceSecretsPatch {
  headers?: Record<string, string | null>
  query?: Record<string, string | null>
  basic?: { username: string; password: string } | null
}

/** Write-only credentials (never returned; `secret_names` lists what is stored). */
export interface DataSourceSecrets {
  headers?: Record<string, string>
  query?: Record<string, string>
  basic?: { username: string; password: string }
}

export interface DataSourceSnapshotMeta {
  version: number
  fetched_at: string
  size_bytes: number
  http_status: number | null
}

/** `GET /data-sources/{id}`. */
export interface DataSource {
  id: ULID
  name: string
  /** Alias used in templates and element `source` props (`^[a-z][a-z0-9_]{0,31}$`). */
  key: string
  type: DataSourceType
  config: DataSourceConfig
  /** e.g. `headers.Authorization`, `query.api_key`, `basic`. */
  secret_names: string[]
  /** Derived from the stored secrets (values are never returned). */
  auth_preset?: AuthPreset
  offline?: DataSourceOffline
  freshness?: DataSourceFreshness
  refresh_s: number
  ttl_s: number
  enabled: boolean
  status: DataSourceStatus
  last_fetched_at: string | null
  last_success_at: string | null
  last_error: string | null
  consecutive_failures: number
  latest: DataSourceSnapshotMeta | null
  used_by: { slideshows: { id: ULID; name: string }[] }
  created_at: string
  updated_at: string
}

/** `POST /data-sources/test`. */
export interface DataSourceTestResult {
  ok: boolean
  http_status: number | null
  duration_ms: number
  size_bytes: number
  payload: unknown
  truncated: boolean
  error: string | null
  /** `datetime` only: the values computed now. */
  values?: DateTimeValues
}

/** `GET /data-sources/{id}/snapshot` (`data: null` before the first successful fetch). */
export interface DataSourceSnapshot {
  version: number
  fetched_at: string
  stale: boolean
  payload: unknown
  freshness?: DataSourceFreshness
}

export type ExploreNodeType = 'object' | 'array' | 'string' | 'integer' | 'number' | 'boolean' | 'null'
export type ExploreFormat = 'image' | 'url' | 'datetime' | 'date' | 'numeric'

/** One node of `GET /data-sources/{id}/explore`. */
export interface ExploreNode {
  name: string
  /** Binding path (`menu.products[?id=123].name`); null when it can't be bound. */
  path: string | null
  type: ExploreNodeType
  /** Scalar sample (strings ≤ 200 chars). */
  sample?: string | number | boolean | null
  format?: ExploreFormat | null
  /** Number of children (object / array). */
  count?: number
  item_type?: string
  /** Arrays of objects: fields that identify items uniquely. */
  key_candidates?: string[]
  key_field?: string | null
  keyed?: boolean
  key_error?: string
  /** Keyed array items: the identifier value. */
  key?: string
  label?: string | null
  /** Array items: the position. */
  index?: number
  unbindable?: true
  children?: ExploreNode[]
  offset?: number
  limit?: number
  /** More children exist (load the next page with `offset`). */
  truncated?: boolean
}

export interface DataSourceExplore {
  source: { id: ULID; key: string; type: DataSourceType }
  snapshot: { version: number; fetched_at: string; size_bytes: number } | null
  freshness: DataSourceFreshness
  /** True for `datetime`: values are computed on players (shown as of now). */
  computed: boolean
  node: ExploreNode | null
}

/* ------------------------------------------------- Phase 12: analytics & proof of play */

export interface AnalyticsRange {
  from: string
  to: string
  timezone: string
  granularity: 'hour' | 'day'
  series_timezone: string
  source: 'hour' | 'day'
  clamped: boolean
}

/** Play metrics are null when the plan lacks `proof_of_play`. */
export interface AnalyticsMetrics {
  impressions: number | null
  plays: number | null
  interrupted: number | null
  skipped: number | null
  errors: number | null
  play_ms: number | null
  online_s: number
  offline_s: number
  uptime_ratio: number | null
  sync_failures: number
  player_errors: number
}

export interface AnalyticsPoint extends AnalyticsMetrics {
  /** ISO hour (UTC) or local date `YYYY-MM-DD`. */
  bucket: string
  start: string
}

export interface AnalyticsOverview {
  range: AnalyticsRange
  features: { proof_of_play: boolean }
  kpis: AnalyticsMetrics & { displays_reporting: number | null; displays_total: number }
  series: AnalyticsPoint[]
}

export interface DisplayAnalytics extends AnalyticsMetrics {
  id: string
  name: string
  location: NamedRef | null
  connectivity: 'online' | 'offline' | 'unknown'
  deleted: boolean
}

export interface ContentAnalyticsRow {
  id: string
  name: string | null
  impressions: number
  plays: number
  interrupted: number
  skipped: number
  errors: number
  play_ms: number
  displays: number
}

export interface ContentAnalytics {
  range: AnalyticsRange
  slideshows: ContentAnalyticsRow[]
  campaigns: ContentAnalyticsRow[]
  top_slides: { slideshow_id: string; slideshow_name: string | null; slide_id: string; impressions: number; play_ms: number }[]
}

export type PlayResult = 'completed' | 'interrupted' | 'skipped' | 'error'

export interface ProofOfPlayRecord {
  id: string
  display: NamedRef
  location: NamedRef | null
  player_id: string
  slideshow: NamedRef | null
  presentation_version_id: string
  slide_id: string
  zone_id: string | null
  campaign: NamedRef | null
  schedule_id: string | null
  channel_id: string | null
  started_at: string
  ended_at: string
  duration_ms: number
  result: PlayResult
  clock_untrusted: boolean
}

export interface ProofOfPlayExport {
  id: string
  status: 'pending' | 'processing' | 'ready' | 'failed' | 'expired'
  format: 'csv'
  from: string
  to: string
  filters: Record<string, string>
  row_count: number | null
  size_bytes: number | null
  error: string | null
  created_at: string | null
  completed_at: string | null
  expires_at: string | null
  url: string | null
}

/* ------------------------------------------------- Phase 12: integrations */

export interface TenantBranding {
  accent_color: string | null
  background_color: string
  logo_url: string | null
  logo_sha256: string | null
}

export interface ScreenshotPolicy {
  mode: 'on_demand' | 'interval'
  interval_s: number | null
}

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'critical'
export type NotificationChannel = 'in_app' | 'push' | 'mail'
export type NotificationDigest = 'immediate' | 'hourly' | 'daily' | 'off'

export interface AppNotification {
  id: ULID
  type: string
  severity: NotificationSeverity
  title: string
  body: string
  url: string | null
  data: Record<string, unknown>
  read_at: string | null
  created_at: string
}

export interface NotificationPreference {
  event_type: string
  label: string
  group: string
  severity?: NotificationSeverity
  channels: Record<NotificationChannel, boolean>
  digest?: NotificationDigest
  available_channels: string[]
}

export interface Webhook {
  id: ULID
  name: string
  url: string
  events: string[]
  is_active: boolean
  consecutive_failures: number
  failing_since: string | null
  disabled_at: string | null
  disabled_reason: string | null
  last_success_at: string | null
  last_failure_at: string | null
  previous_secret_expires_at: string | null
  created_at: string | null
  /** Only on create / rotate. */
  secret?: string
}

export type WebhookDeliveryStatus = 'pending' | 'succeeded' | 'retrying' | 'failed' | 'dead'

export interface WebhookDelivery {
  id: ULID
  event_id: ULID
  event_type: string
  status: WebhookDeliveryStatus
  attempt: number
  response_status: number | null
  response_excerpt: string | null
  duration_ms: number | null
  error: string | null
  next_attempt_at: string | null
  delivered_at: string | null
  created_at: string | null
  updated_at: string | null
  payload?: Record<string, unknown>
}

export interface WebhookEventType {
  type: string
  label: string
}

export interface ApiKey {
  id: ULID
  name: string
  environment: 'live' | 'test'
  prefix: string
  scopes: string[]
  allowed_ips: string[] | null
  expires_at: string | null
  last_used_at: string | null
  last_used_ip: string | null
  revoked_at: string | null
  status: 'active' | 'expired' | 'revoked'
  created_by: { id: ULID; name: string } | null
  created_at: string | null
  /** Plaintext, only in the create response. */
  key?: string
}

export interface ApiKeyScope {
  scope: string
  label: string
  permissions: string[]
  grantable: boolean
}

export interface BrandingSettings extends TenantBranding {
  enabled: boolean
}

/* ------------------------------------------------------------ Phase B --- */
/* Variables — docs/VARIABLES.md §6. */

export const VARIABLE_TYPES = ['text', 'number', 'boolean', 'date', 'datetime', 'url', 'media', 'color', 'json'] as const
export type VariableType = (typeof VARIABLE_TYPES)[number]

/** Scopes below tenant at which values may be set. */
export const OVERRIDE_SCOPES = ['location', 'display_group', 'display', 'campaign'] as const
export type OverrideScope = (typeof OVERRIDE_SCOPES)[number]
export type VariableScopeType = 'tenant' | OverrideScope
export type VariableValueSource = 'user' | 'import' | 'flow'
export type VariableMediaKind = 'image' | 'svg' | 'video'

export interface VariableValidation {
  required?: boolean
  min?: number | string
  max?: number | string
  max_length?: number
  regex?: string
  enum?: unknown[]
  kinds?: VariableMediaKind[]
}

export interface Variable {
  id: ULID
  key: string
  name: string
  description: string | null
  group: string | null
  type: VariableType
  validation: VariableValidation
  default_value: unknown
  overridable_at: OverrideScope[]
  editable_by: string | null
  tenant_value: unknown
  overrides_count: number
  created_at: string
  updated_at: string
}

export type VariableBuiltinNamespace = 'tenant' | 'location' | 'display' | 'campaign'

export interface VariableBuiltin {
  namespace: VariableBuiltinNamespace
  key: string
  label: string
  type: VariableType | string
}

export interface VariableValue {
  id: ULID
  variable_id: ULID
  scope_type: VariableScopeType
  scope_id: ULID
  scope_name: string | null
  value: unknown
  source: VariableValueSource
  runtime: boolean
  expires_at: string | null
  updated_by: { id: ULID; name: string } | null
  updated_at: string
}

/** Where a resolved value comes from (VARIABLES.md §3). */
export type ResolvedScopeType = 'runtime' | VariableScopeType | 'platform' | 'default' | 'none'

export interface ResolvedSource {
  scope_type: ResolvedScopeType
  scope_id: string | null
  name: string | null
  runtime: boolean
  expires_at: string | null
}

export interface ResolvedVariable {
  variable_id: ULID
  key: string
  name: string
  group: string | null
  type: VariableType
  value: unknown
  source: ResolvedSource
  /** The value set exactly at the requested scope. */
  own: { value: unknown; source: VariableValueSource; runtime: boolean; expires_at: string | null; updated_at: string | null } | null
  /** What applies once the own value is reset. */
  inherited: { value: unknown; source: ResolvedSource }
  /** The current user may set a value at this scope. */
  editable: boolean
}

export type ResolveScopeType = 'tenant' | 'location' | 'display_group' | 'display' | 'campaign'

export interface VariableResolution {
  context: { scope_type: ResolveScopeType; scope_id: string | null; name: string | null }
  values: ResolvedVariable[]
  builtins: Partial<Record<VariableBuiltinNamespace, Record<string, unknown>>>
}

export type VariableBulkChange =
  | { variable_id: ULID; scope_type: VariableScopeType; scope_id: ULID; value: unknown }
  | { variable_id: ULID; scope_type: VariableScopeType; scope_id: ULID; reset: true }

export interface VariableImportReport {
  dry_run: boolean
  created: number
  updated: number
  values_set: number
  errors: { row: number | string | null; field: string | null; message: string }[]
}

export interface VariableExport {
  variables: Record<string, unknown>[]
  values: { key: string; scope_type: VariableScopeType; scope_id: string | null; scope_name: string | null; value: unknown }[]
}

/* ------------------------------------------------------------ Phase D --- */
/* Signetix Logic — rule library (SIGNETIX_LOGIC.md §8). */

/** `condition` of a campaign / schedule / channel item. */
export interface RuleCondition {
  rule_id: ULID
  name: string
  unknown_as: boolean
  edge: boolean
}

export interface RuleReference {
  ref: string
  known: boolean
  type: string | null
  label: string | null
}

export interface LibraryRule {
  id: ULID
  name: string
  description: string | null
  /** `signetix.rule` v1 document. */
  rule: Record<string, unknown>
  edge: boolean
  cloud_refs: string[]
  references: RuleReference[]
  usage: { campaigns: number; schedules: number; channel_items: number }
  created_at: string
  updated_at: string
}

export interface RuleValidationError {
  path: string
  code: string
  message: string
}

export interface RuleValidation {
  valid: boolean
  errors: RuleValidationError[]
  edge: boolean
  cloud_refs: string[]
  references: RuleReference[]
}

/** `GET /rules/catalogue` entry. */
export interface RuleCatalogueEntry {
  ref: string
  label: string
  type: string
  group: string
  namespace: string
  /** Data sources: drill into paths with `/data-sources/{source_id}/explore`. */
  source_id?: ULID | null
}
